'use client';

import { Identity } from '@/containers/Identities/types';
import { useAddOptOut } from '@/containers/OptOutManager/api';
import { IdentitySelector } from '@/shared/components/IdentitySelector/IdentitySelector';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useCallback, useState } from 'react';

interface StateTabProps {
  accountId: string;
  identities: Identity[];
  showIdentitySelector: boolean;
  onSuccess?: () => void;
  onClose: () => void;
}

// US States list
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

export const StateTab: React.FC<StateTabProps> = ({
  accountId,
  identities,
  showIdentitySelector,
  onSuccess,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();
  const addOptOutMutation = useAddOptOut();

  const [selectedIdentityId, setSelectedIdentityId] = useState<string>(
    identities.length === 1 ? identities[0].identity_id : ''
  );
  const [selectedState, setSelectedState] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState<{
    state?: string;
    identity?: string;
  }>({});

  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!selectedState) {
      newErrors.state = 'Please select a state';
    }

    if (showIdentitySelector && !selectedIdentityId) {
      newErrors.identity = 'Please select an identity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedState, selectedIdentityId, showIdentitySelector]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        const stateInfo = US_STATES.find((state) => state.code === selectedState);

        await addOptOutMutation.mutateAsync({
          account_id: accountId,
          identity_id: selectedIdentityId || identities[0]?.identity_id || accountId,
          dnc_type: 'STATE',
          value: selectedState,
          reason: `State opt-out: ${stateInfo?.name || selectedState}`,
        });

        showSuccess(
          `Successfully added state opt-out for ${stateInfo?.name || selectedState}`
        );
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Failed to add state opt-out:', error);
        showError('Failed to add state opt-out. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      selectedState,
      accountId,
      selectedIdentityId,
      identities,
      addOptOutMutation,
      showSuccess,
      showError,
      onSuccess,
      onClose,
    ]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Identity Selection - Only show if multiple identities */}
      {showIdentitySelector && (
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Identity</span>
          </label>
          <IdentitySelector
            identities={identities}
            value={selectedIdentityId}
            onChange={(e) => {
              setSelectedIdentityId(e.target.value);
              if (errors.identity) {
                setErrors((prev) => ({ ...prev, identity: undefined }));
              }
            }}
            currentAccountId={accountId}
            className={errors.identity ? 'select-error' : ''}
            placeholder="Select an identity"
          />
          {errors.identity && (
            <label className="label">
              <span className="text-error label-text-alt">{errors.identity}</span>
            </label>
          )}
        </div>
      )}

      {/* State Selection */}
      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">State</span>
        </label>
        <select
          className={`select select-bordered w-full text-sm ${
            errors.state ? 'select-error' : ''
          }`}
          value={selectedState}
          onChange={(e) => {
            setSelectedState(e.target.value);
            if (errors.state) {
              setErrors((prev) => ({ ...prev, state: undefined }));
            }
          }}>
          <option value="">Select a state</option>
          {US_STATES.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
        {errors.state && (
          <label className="label">
            <span className="text-error label-text-alt">{errors.state}</span>
          </label>
        )}
        <label className="label">
          <span className="label-text-alt text-gray-500">
            Select a US state to opt-out all numbers from that state
          </span>
        </label>
      </div>

      {/* Info Box */}
      <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <div className="flex h-5 w-5 items-center justify-center rounded-full text-blue-600 dark:text-blue-400">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="font-medium">State Opt-Out</div>
            <div>
              This will opt-out all phone numbers from the selected state. No reason is
              required for state opt-outs as the reason will be automatically set.
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
          disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add State Opt-Out'}
        </button>
      </div>
    </form>
  );
};
